import { Suspense, useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation } from '@tanstack/react-router';
import {
  LayoutDashboard, Factory, Package, BarChart3, Tag, Boxes,
  Handshake, Settings, Search, LogOut, Menu, Moon, Sun, Lock, Circle, ScanLine,
  ReceiptText,
  type LucideIcon,
} from 'lucide-react';
import { cn } from './ui';
import { isDark, toggleTheme } from '../lib/theme';
import { useMe, useLogout, ROLE_LABEL } from '../features/auth/api';
import type { RunningTimer } from '../features/timer/api';
import { askConfirm } from './dialogs';
import { api } from '../lib/api';
import { outbox } from '../lib/offline';
import { toast } from '../lib/toast';
import { NAV_SECTIONS, NAV_FLAT, visibleItems, areaForPath, tabForPath, type Capability } from './nav';
import CommandPalette from './CommandPalette';
import ErrorBoundary from './ErrorBoundary';
import { DialogHost } from './dialogs';
import NotificationBell from './NotificationBell';
import TimerBar from '../features/timer/TimerBar';
import PwaBar from './PwaBar';
import ThemePicker from './ThemePicker';
import LanguageToggle from './LanguageToggle';
import { useT } from '../lib/i18n';

// Mapování oblast → linková ikona (lucide). Wordmark a chrome jsou monochrom.
const NAV_ICON: Record<string, LucideIcon> = {
  prehled: LayoutDashboard,
  prace: Factory,
  zakazky: Package,
  pujcovna: Tag,
  sklad: Boxes,
  kontakty: Handshake,
  reporty: BarChart3,
  koordinace: ReceiptText,
  sprava: Settings,
};

function ThemeToggle() {
  const [dark, setDark] = useState(isDark());
  const t = useT();
  const Icon = dark ? Sun : Moon;
  return (
    <button
      onClick={() => setDark(toggleTheme())}
      className="grid h-9 w-9 place-items-center rounded-md border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-600 dark:text-zinc-300"
      title={dark ? t('Světlý režim') : t('Tmavý režim')}
      aria-label="Přepnout motiv"
    >
      <Icon size={16} />
    </button>
  );
}

function SidebarUser() {
  const { data: user } = useMe();
  const logout = useLogout();
  const t = useT();
  if (!user) return null;
  // Sdílený tablet: stopky by běžely dál na jméno toho, kdo odešel, a nikdo by o nich nevěděl.
  const signOut = async () => {
    const timer = await api.get<{ timer: RunningTimer | null }>('/api/timer').then((r) => r.timer).catch(() => null);
    if (timer) {
      const stop = await askConfirm({
        title: 'Běží ti stopky',
        body: `${timer.phase.propItem.name}. Zastavím je a zapíšu čas, jinak by běžely dál na tvoje jméno.`,
        confirmLabel: 'Zastavit a odhlásit',
        cancelLabel: 'Zpět',
      });
      if (!stop) return;
      await api.post('/api/timer/stop', {});
    }
    const waiting = outbox.size();
    if (waiting > 0) toast.info(`Neodeslané změny (${waiting}) počkají, až se znovu přihlásíš.`);
    logout.mutate();
  };
  const initial = user.fullName.trim().charAt(0).toUpperCase() || '?';
  const sub = user.defaultRoleLabel || ROLE_LABEL[user.role];
  return (
    <div className="mt-auto flex items-center gap-2.5 border-t border-zinc-700/60 px-2 pt-3">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-zinc-700 text-xs font-semibold text-zinc-100">{initial}</span>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-[13px] text-zinc-200">{user.fullName}</div>
        <div className="truncate text-[11px] text-zinc-500">{sub}</div>
      </div>
      <button
        onClick={() => void signOut()}
        disabled={logout.isPending}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-zinc-400 hover:bg-white/10 hover:text-zinc-100"
        title={t('Odhlásit')}
        aria-label={t('Odhlásit')}
      >
        <LogOut size={15} />
      </button>
    </div>
  );
}

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
  const t = useT();
  const pathname = useLocation({ select: (l) => l.pathname });
  const { data: user } = useMe();
  const caps = user?.caps ?? [];
  const activeArea = areaForPath(pathname)?.area.id;
  // Oblast je vidět, když má uživatel nárok aspoň na jednu její stránku;
  // odkaz vede na první takovou.
  const sections = NAV_SECTIONS
    .map((s) => ({
      ...s,
      areas: s.areas
        .map((a) => ({ ...a, tabs: visibleItems(a.tabs, caps) }))
        .filter((a) => a.tabs.length > 0),
    }))
    .filter((s) => s.areas.length > 0);
  return (
    <nav className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4">
      {sections.map((section) => (
        <div key={section.title}>
          <div className="px-2.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
            {t(section.title)}
          </div>
          <div className="space-y-0.5">
            {section.areas.map((area) => {
              const Icon = NAV_ICON[area.id] ?? Circle;
              const active = area.id === activeArea;
              return (
                <Link
                  key={area.id}
                  to={area.tabs[0].to}
                  onClick={onNavigate}
                  // --sec = barva sekce; uplatní se jen v motivu „podle modulu" (viz index.css).
                  style={{ ['--sec' as string]: section.color ?? 'transparent' }}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'navlink relative flex items-center gap-3 rounded-md px-2.5 py-2 text-[13px]',
                    active ? 'navlink-active bg-white/10 font-medium text-white' : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-100',
                  )}
                >
                  <span className="navlink-stripe" aria-hidden />
                  <Icon size={17} className="shrink-0" strokeWidth={1.75} />
                  {t(area.label)}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

// Záložky stránek aktuální oblasti (Půjčovna: Položky / Rezervace / …).
// U oblasti s jedinou stránkou se neukazují.
function AreaTabs() {
  const t = useT();
  const pathname = useLocation({ select: (l) => l.pathname });
  const { data: user } = useMe();
  const found = areaForPath(pathname);
  if (!found || !user) return null;
  const tabs = visibleItems(found.area.tabs, user.caps);
  if (tabs.length < 2) return null;
  const current = tabForPath(tabs, pathname);
  return (
    <div className="-mx-4 -mt-4 mb-5 overflow-x-auto border-b border-zinc-200 px-4 dark:border-zinc-800 md:-mx-6 md:-mt-6 md:mb-6 md:px-6">
      <nav className="flex gap-1" aria-label={t(found.area.label)}>
        {tabs.map((tab) => {
          const active = tab.to === current?.to;
          return (
            <Link
              key={tab.to}
              to={tab.to}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'whitespace-nowrap border-b-2 px-3 py-3 text-sm',
                active
                  ? 'border-zinc-900 font-medium text-zinc-900 dark:border-zinc-100 dark:text-zinc-100'
                  : 'border-transparent text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200',
              )}
            >
              {t(tab.label)}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

// Skener je na mobilu po ruce jako výrazné tlačítko — v menu je schovaný
// v „Moje práce" a v dílně se po něm sahá pořád.
function ScanFab() {
  const pathname = useLocation({ select: (l) => l.pathname });
  const { data: user } = useMe();
  // Na stránkách se skenerem by tlačítko jen překáželo (zakrývá potvrzení).
  if (!user?.caps.includes('production') || pathname === '/skener' || pathname === '/sklad/sken') return null;
  return (
    <Link
      to="/skener"
      aria-label="Skenovat QR"
      className="fixed bottom-5 right-5 z-30 grid h-14 w-14 place-items-center rounded-full bg-zinc-900 text-white shadow-lg dark:bg-zinc-100 dark:text-zinc-900 md:hidden"
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ScanLine size={24} />
    </Link>
  );
}

function Wordmark() {
  return (
    <div className="flex items-center gap-2.5 px-4 h-14 border-b border-zinc-700/60">
      <span className="grid h-7 w-7 place-items-center rounded-lg bg-zinc-100 text-zinc-900 text-sm font-bold">S</span>
      <div className="leading-none">
        <div className="text-[13px] font-semibold tracking-[0.03em] text-zinc-100">STUDIO OS</div>
        <div className="mt-0.5 text-[9px] tracking-[0.14em] text-zinc-500">VÝROBA · PŮJČOVNA</div>
      </div>
    </div>
  );
}

// Detailové stránky mimo hlavní navigaci (guard podle prefixu cesty).
const EXTRA_PATH_CAPS: Array<{ prefix: string; cap: Capability }> = [
  { prefix: '/vyroby', cap: 'production' },
];

// Najde oprávnění požadované pro aktuální cestu (dle nav konfigurace).
function capForPath(pathname: string): Capability | undefined {
  return (
    NAV_FLAT.find((i) => i.cap && (pathname === i.to || pathname.startsWith(i.to + '/')))?.cap ??
    EXTRA_PATH_CAPS.find((e) => pathname.startsWith(e.prefix))?.cap
  );
}

// Zablokuje stránku, na kterou uživatel nemá oprávnění (přímý vstup přes URL).
function GuardedOutlet() {
  const pathname = useLocation({ select: (l) => l.pathname });
  const { data: user } = useMe();
  const need = capForPath(pathname);
  if (user && need && !user.caps.includes(need)) {
    // Koordinátor bez provozu: z dashboardu rovnou na jeho domovskou agendu.
    if (pathname === '/' && user.caps.includes('suppliers')) {
      return <Navigate to="/nabidky-dodavatelu" replace />;
    }
    return (
      <div className="mx-auto mt-16 max-w-md rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-8 text-center">
        <Lock size={26} className="mx-auto text-zinc-400" />
        <h1 className="mt-3 font-semibold">Nemáš oprávnění</h1>
        <p className="mt-1 text-sm text-zinc-500">Tato část je dostupná jen pro vyšší roli. Obrať se na vedení.</p>
        <Link to="/" className="mt-4 inline-block rounded-md bg-zinc-900 px-4 py-2 text-sm text-white dark:bg-zinc-100 dark:text-zinc-900">Zpět na dashboard</Link>
      </div>
    );
  }
  return <Outlet />;
}

export default function Layout() {
  const pathname = useLocation({ select: (l) => l.pathname });
  // Stopky jsou dílenský nástroj — bez oprávnění na provoz je nezobrazujeme.
  const { data: layoutUser } = useMe();
  const maProvoz = !!layoutUser?.caps.includes('production');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const t = useT();

  // Globální ⌘K / Ctrl+K otevře command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <div className="flex">
        {/* Antracitová lišta – desktop */}
        <aside className="hidden md:flex w-60 shrink-0 flex-col bg-zinc-900 h-screen sticky top-0">
          <Wordmark />
          <NavLinks />
          <div className="px-2.5 pb-3"><SidebarUser /></div>
        </aside>

        {/* Drawer – mobil */}
        {drawerOpen && (
          <div className="fixed inset-0 z-40 md:hidden" onClick={() => setDrawerOpen(false)}>
            <div className="absolute inset-0 bg-black/50" />
            <aside className="absolute left-0 top-0 flex h-full w-64 flex-col bg-zinc-900" onClick={(e) => e.stopPropagation()}>
              <Wordmark />
              <NavLinks onNavigate={() => setDrawerOpen(false)} />
              <div className="flex items-center gap-2.5 px-4 pb-3"><LanguageToggle /><ThemePicker /><ThemeToggle /></div>
              <div className="px-2.5 pb-3"><SidebarUser /></div>
            </aside>
          </div>
        )}

        {/* Hlavní sloupec */}
        <div className="flex-1 min-w-0 flex flex-col h-screen">
          <header className="h-14 shrink-0 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-3 px-4 sticky top-0 bg-zinc-50/90 dark:bg-zinc-950/90 backdrop-blur z-10">
            <button
              onClick={() => setDrawerOpen(true)}
              className="md:hidden grid h-9 w-9 place-items-center rounded-md border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              aria-label="Menu"
            >
              <Menu size={18} />
            </button>

            {/* Vyhledávací / ⌘K tlačítko */}
            <button
              onClick={() => setPaletteOpen(true)}
              aria-label={t('Hledat nebo skočit…')}
              className="flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm text-zinc-400 hover:border-zinc-400 dark:hover:border-zinc-600 sm:w-full sm:max-w-md sm:justify-start sm:px-3"
            >
              <Search size={15} />
              <span className="hidden flex-1 truncate text-left sm:inline">{t('Hledat nebo skočit…')}</span>
              <kbd className="hidden sm:inline rounded border border-zinc-300 dark:border-zinc-700 px-1.5 text-[10px] text-zinc-400">⌘K</kbd>
            </button>

            <div className="ml-auto flex min-w-0 items-center gap-2.5">
              {maProvoz && <TimerBar />}
              <NotificationBell />
              {/* Na telefonu patří záhlaví stopkám, nastavení vzhledu je v menu. */}
              <div className="hidden items-center gap-2.5 md:flex"><LanguageToggle /><ThemePicker /><ThemeToggle /></div>
            </div>
          </header>

          <PwaBar />

          <main className={cn('flex-1 overflow-auto p-4 pb-24 md:p-6')}>
            <AreaTabs />
            {/* Pád stránky nesmí sebrat i navigaci — jinak se uživatel nemá kam vrátit.
                key = cesta: po přechodu jinam se hranice resetuje sama. */}
            <ErrorBoundary key={pathname} scope={pathname}>
              <Suspense fallback={<div className="flex h-40 items-center justify-center text-sm text-zinc-400">Načítám…</div>}>
                <GuardedOutlet />
              </Suspense>
            </ErrorBoundary>
          </main>
        </div>
      </div>

      <ScanFab />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <DialogHost />
    </div>
  );
}
